import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  CreateCounterpartyRequest,
  MergerFilter,
  TradingCounterParty,
  TradingCounterPartyProgress
} from "../../../api/Api";
import { buildProgressOverride } from "../../../components/lifecycle/progressOverride/buildProgressOverride";

export type CounterpartyStatusFilter = "active" | "archived" | "all";

export const counterpartyKeys = {
  all: ["counterparties"] as const,
  overviews: () => [...counterpartyKeys.all, "overview"] as const,
  overview: (
    status: CounterpartyStatusFilter = "active",
    merger: MergerFilter = "exclude"
  ) => [...counterpartyKeys.overviews(), status, merger] as const,
  completedOverview: (merger: MergerFilter = "exclude") =>
    [...counterpartyKeys.overviews(), "completed", merger] as const,
  specific: (id: string) => [...counterpartyKeys.all, "specific", id] as const,
  assignees: (groupName: string) =>
    [...counterpartyKeys.all, "assignees", groupName] as const
};

// Mergers live in their own tab, so the TC grids leave them out (the backend default too).
export const useCounterpartyOverview = (
  status: CounterpartyStatusFilter = "active",
  merger: MergerFilter = "exclude"
) => {
  return useQuery({
    queryKey: counterpartyKeys.overview(status, merger),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.overview.overview({ status, merger });
      return response.data;
    }
  });
};

export const useCompletedCounterpartyOverview = (
  merger: MergerFilter = "exclude"
) => {
  return useQuery({
    queryKey: counterpartyKeys.completedOverview(merger),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.completedOverview.completedOverview({
        merger
      });
      return response.data;
    }
  });
};

export const useSpecificCounterparty = (
  id?: string,
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: counterpartyKeys.specific(id ?? "not_set"),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.specific.specific({
        id: id ?? "id_should_always_exist"
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!id,
    refetchInterval: options?.refetchInterval
  });
};

export const useAssignees = (groupName: string) => {
  return useQuery({
    queryKey: counterpartyKeys.assignees(groupName),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.assignees.assignees({ groupName });
      return response.data;
    },
    enabled: !!groupName
  });
};

//mutations

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useCreateCounterparty = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    // Resolves to the new counterparty's id (the merger form uploads attachments to it).
    mutationFn: async (request: CreateCounterpartyRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.create.create(request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: counterpartyKeys.all });
      if (options?.onSuccess) {
        options.onSuccess();
      }
    }
  });
};

export const useArchiveCounterparty = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.archive.archive({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: counterpartyKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRestoreCounterparty = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.restore.restore({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: counterpartyKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateCounterparty = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async (counterparty: TradingCounterParty) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.update.update(counterparty);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.id) {
        //optimisic update of the specific counterparty query data
        queryClient.setQueryData(
          counterpartyKeys.specific(variables.id),
          variables
        );
        // Then refetch: the server stamps a new version on every save and merges the next save
        // against the version it carries. Keeping the one sent here would make undoing your own
        // earlier change look like no change (ADHO-496).
        queryClient.invalidateQueries({
          queryKey: counterpartyKeys.specific(variables.id)
        });
      }

      queryClient.invalidateQueries({
        queryKey: counterpartyKeys.overviews()
      });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error as Error);
      }
    }
  });
};

/** The admin progress dropdown in a counterparty lifecycle dialog. Its own mutation, so it never closes the dialog. */
export const useCounterpartyProgressOverride = (
  counterparty: TradingCounterParty,
  progressKey: keyof TradingCounterPartyProgress
) => buildProgressOverride(counterparty, progressKey, useUpdateCounterparty());
